<script setup>
/* global axios */
import { computed, ref, watch, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { operationFeedback } from 'dashboard/helper/agentFeedback.mjs';
import { displayDateTime } from 'shared/helpers/timeHelper';

const props = defineProps({ show: Boolean, kind: String, messageId: Number, conversationId: Number, content: String });
const emit = defineEmits(['close']);
const route = useRoute();
const busy = ref(false), error = ref(''), note = ref(''), details = ref(null), value = ref(''), mode = ref('');
const shown = computed({ get: () => props.show, set: v => { if (!v) emit('close'); } });
const url = computed(() => `/api/v1/accounts/${route.params.accountId}/conversations/${props.conversationId}/messages/${props.messageId}`);
const labels = { queued: '等待渠道确认', confirmed: '已确认', failed: '未成功', unknown: '结果待核对', edit: '编辑', reaction: '表情回应', revoke: '撤回', reconcile: '核对投递', retry: '安全重试' };
const ackNames = { '-1': '渠道发送失败', 0: '尚无回执', 1: '已发送', 2: '已送达', 3: '对方已读', 4: '语音已播放' };
const timeNames = { sent: '发送', delivered: '送达', read: '已读', played: '播放', failed: '失败' };
const reasons = { mapping_missing: '原始消息映射缺失', ambiguous_mapping: '包含多条渠道消息，暂不支持此操作', provider_rejected: 'WhatsApp 未接受操作', outcome_unknown: '渠道未返回明确结果，请核对，不要重复发送', no_safe_retry: '已有发送尝试，已阻止重复发送', expired: '超过编辑时限', pending_parts: '部分消息仍缺少回执', receipt_checked: '已核对现有回执' };
const formatTime = n => n ? displayDateTime(n * 1000) : '未取得时间';
let timer, polls = 0, generation = 0;
const stop = () => { clearTimeout(timer); generation += 1; };
async function refresh() {
  const version = generation;
  try {
    const { data } = await axios.get(`${url.value}/bcw_details`);
    if (version !== generation || !props.show) return;
    details.value = data;
    if (data.operations?.some(o => o.state === 'queued') && polls++ < 15) timer = setTimeout(refresh, 2000);
  } catch { if (version === generation) error.value = '消息详情加载失败，请重试'; }
}
watch(() => props.show, show => {
  stop(); polls = 0;
  if (!show) return;
  mode.value = props.kind; value.value = props.kind === 'edit' ? props.content || '' : '';
  error.value = ''; note.value = ''; details.value = null; refresh();
});
onBeforeUnmount(stop);
async function submit(kind, text = '') {
  if (busy.value) return;
  busy.value = true; error.value = ''; note.value = '';
  try {
    await axios.post(`${url.value}/bcw_operation`, { kind, value: text, request_key: crypto.randomUUID() });
    note.value = '已提交，等待 WhatsApp 确认。'; mode.value = 'details'; polls = 0; clearTimeout(timer); await refresh();
  } catch (e) { error.value = operationFeedback(e.response?.data?.error); }
  finally { busy.value = false; }
}
</script>

<template>
  <woot-modal v-model:show="shown" :on-close="() => emit('close')">
    <section class="p-6 text-n-slate-12 flex flex-col gap-4">
      <h3 class="text-lg font-semibold">{{ mode === 'edit' ? '编辑已发送消息' : mode === 'reaction' ? '表情回应' : mode === 'revoke' ? '撤回消息' : '消息详情' }}</h3>
      <template v-if="mode === 'edit'">
        <textarea v-model="value" aria-label="修改消息内容" rows="6" maxlength="16000" class="w-full rounded-lg border border-n-weak p-3" />
        <p class="text-xs text-n-slate-11">发送后 15 分钟内可编辑纯文字消息，渠道确认后会更新原消息。</p>
        <button :disabled="busy || !value.trim()" class="button" @click="submit('edit', value)">保存修改</button>
      </template>
      <template v-else-if="mode === 'reaction'">
        <div class="flex flex-wrap gap-3">
          <button v-for="emoji in ['👍','❤️','😂','😮','😢','🙏']" :key="emoji" :aria-label="`回应 ${emoji}`" :disabled="busy" class="text-2xl p-2 rounded-lg hover:bg-n-alpha-2" @click="submit('reaction', emoji)">{{ emoji }}</button>
        </div>
        <button :disabled="busy" class="text-sm text-n-slate-11" @click="submit('reaction', '')">移除我的回应</button>
      </template>
      <template v-else-if="mode === 'revoke'">
        <p>确认从 WhatsApp 撤回此消息？渠道确认后显示灰色撤回提示。</p>
        <button :disabled="busy" class="button" @click="submit('revoke')">确认撤回</button>
      </template>
      <template v-else>
        <p v-if="!details">正在加载消息详情…</p>
        <template v-else>
          <p class="text-sm">{{ details.direction === 'incoming' ? '客户发来的消息' : '我方发出的消息' }} · {{ formatTime(details.message_time) }}</p>
          <p v-if="!details.parts?.length && details.direction === 'outgoing'" class="text-sm">尚未取得渠道回执，不能确认送达或已读。</p>
          <div v-for="part in details.parts" :key="part.part" class="rounded-lg bg-n-alpha-2 p-3 text-sm">
            <p>第 {{ part.part }} 部分 · {{ part.revoked ? '已撤回' : ackNames[part.ack] }}</p>
            <p v-for="(stamp, state) in part.times" :key="state" class="text-xs text-n-slate-11 mt-1">{{ timeNames[state] }}回执记录：{{ formatTime(stamp) }}</p>
            <p v-if="!Object.keys(part.times || {}).length" class="text-xs text-n-slate-11 mt-1">历史记录未保存各阶段时间</p>
          </div>
          <p class="text-xs text-n-slate-11">“语音已播放”以对方的 WhatsApp 回执为准。未收到回执时不会推测对方是否播放。</p>
          <div v-for="op in details.operations" :key="op.id" class="text-sm">
            {{ labels[op.kind] }} · {{ labels[op.state] }}
            <span class="block text-xs text-n-slate-11">{{ reasons[op.reason] }} {{ formatTime(op.at) }}</span>
          </div>
          <div class="flex gap-3">
            <button :disabled="busy" class="button" @click="submit('reconcile')">核对投递状态</button>
            <button v-if="details.status === 'failed'" :disabled="busy" class="button secondary" @click="submit('retry')">检查并安全重试</button>
          </div>
        </template>
      </template>
      <p v-if="note" role="status" class="text-sm text-n-slate-11">{{ note }}</p>
      <p v-if="error" role="alert" class="text-sm text-n-ruby-9">{{ error }}</p>
      <button class="text-sm self-end" @click="emit('close')">关闭</button>
    </section>
  </woot-modal>
</template>
